import ClassNames from "classnames";
import { EmblaCarouselType } from "embla-carousel";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { SlideImage } from "yet-another-react-lightbox";
import { selectActiveSite } from "../selectors/sites.js";
import { useAppSelector } from "../store/hooks.js";
import styles from "../stylesheets/components/image-gallery-layout-3.module.css";
import moduleBaseStyles from "../stylesheets/components/module-base.module.css";
import {
  ColorScheme,
  ContainerQueries,
  ImageModuleSettings,
  Language,
  TitleAndSubtitleTextAlign,
  TranslatedModule,
} from "../types/index.js";
import {
  getGalleryMediaAspectRatio,
  getInlineSize,
  getPlaceholderImage,
} from "../utils/utils.js";
import { minWidthBreakpoints } from "./ContainerQueries.js";
import ModuleHeadings from "./ModuleHeadings.js";
import ImageModule from "./Modules/ImageModule.js";
import Slider from "./Slider.js";
import SliderArrow from "./SliderArrow.js";

interface Props {
  title: string;
  subtitle: string;
  textAlign: TitleAndSubtitleTextAlign;
  scheme: ColorScheme;
  isFirstOnPage: boolean;
  imageModules: TranslatedModule<ImageModuleSettings>[];
  queries: ContainerQueries;
  slides: SlideImage[];
  isPreview: boolean;
  languageId: Language;
  pageId: string;
  moduleId: string;
  activeModuleId: string | undefined;
  onClickImage: (index: number) => void;
}

const breakPoints = {
  [minWidthBreakpoints["Query--small"]]: 1,
  [minWidthBreakpoints["Query--medium"]]: 2,
  [minWidthBreakpoints["Query--large"]]: 4,
  [1920]: 6,
};

const ImageGalleryLayout3: FunctionComponent<Props> = ({
  title,
  subtitle,
  textAlign,
  scheme,
  isFirstOnPage,
  imageModules,
  queries,
  slides,
  isPreview,
  languageId,
  pageId,
  moduleId,
  activeModuleId,
  onClickImage,
}) => {
  const [slider, setSlider] = useState<EmblaCarouselType>();
  const ref = useRef<HTMLDivElement>(null);
  const [slidesToShow, setSlidesToShow] = useState(1);
  const rafId = useRef<number>(undefined);

  const site = useAppSelector(selectActiveSite);

  useEffect(() => {
    const onResize = (entries: ResizeObserverEntry[]) => {
      const inlineSize = getInlineSize(entries);
      if (inlineSize === undefined) return;

      const slidesToShow = Object.entries(breakPoints).reduce(
        (acc, [breakPoint, value]) =>
          inlineSize >= Number(breakPoint) ? value : acc,
        1,
      );
      setSlidesToShow(slidesToShow);
    };

    const observer = new ResizeObserver((entries) => {
      rafId.current = window.requestAnimationFrame(() => onResize(entries));
    });

    ref.current && observer.observe(ref.current);

    return () => {
      rafId.current !== undefined && window.cancelAnimationFrame(rafId.current);
      observer.disconnect();
    };
  }, []);

  const images = imageModules.length
    ? imageModules
    : new Array(6)
        .fill(true)
        .map((_, index) =>
          getPlaceholderImage({
            siteId: site.id,
            pageId,
            languageId,
            parentId: moduleId,
            index,
          }),
        );

  return (
    <div ref={ref}>
      <ModuleHeadings
        textAlign={textAlign}
        scheme={scheme}
        isFirstOnPage={isFirstOnPage}
        title={title}
        subtitle={subtitle}
        wrapperClassName={ClassNames(
          moduleBaseStyles.Module__Wrapper,
          moduleBaseStyles["Module__Wrapper--small-padding"],
        )}
      />
      <div className={styles.ImageGalleryLayout3__SliderWrapper}>
        <Slider
          slideClassName={ClassNames({
            [styles.ImageGalleryLayout3__SlideMobile]:
              !queries["Query--medium"],
          })}
          isPreview={isPreview}
          sliderEffect="slide"
          onSliderLoaded={setSlider}
          slidesToShow={slidesToShow}
          activePreviewSlideId={activeModuleId}
          slideIds={images.map(({ id }) => id)}
          loop={true}
          align={queries["Query--medium"] ? "center" : "start"}
        >
          {images.map((imageModule, index) => {
            const item = slides[index];
            return (
              <a
                href={isPreview || !item ? undefined : item.src}
                className={styles.ImageGalleryLayout3__Item}
                key={imageModule.id}
                onClick={(e) => {
                  e.preventDefault();
                  onClickImage(index);
                }}
              >
                <ImageModule
                  aspectRatio={getAspectRatio(index)}
                  sizes="50vw"
                  lazyLoad={true}
                  width={1920}
                  translatedModule={imageModule}
                  isPreview={isPreview}
                  isSlider={false}
                  pageId={pageId}
                  scheme={scheme}
                  showOverlay={false}
                />
              </a>
            );
          })}
        </Slider>
        {slidesToShow < images.length && (
          <div>
            <SliderArrow
              className={styles.ImageGalleryLayout3__SliderArrow}
              iconClassName={styles.ImageGalleryLayout3__SliderArrowIcon}
              glyph="arrow-back"
              direction="left"
              onClick={() => slider?.scrollPrev()}
              scheme={scheme}
              transparentBackground
              languageId={languageId}
            />
            <SliderArrow
              className={ClassNames(
                styles.ImageGalleryLayout3__SliderArrow,
                styles.ImageGalleryLayout3__SliderArrow_Next,
              )}
              iconClassName={styles.ImageGalleryLayout3__SliderArrowIcon}
              glyph="arrow-forward"
              direction="right"
              onClick={() => slider?.scrollNext()}
              scheme={scheme}
              transparentBackground
              languageId={languageId}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const getAspectRatio = (index: number): number =>
  getGalleryMediaAspectRatio(index, "layout-3");

export default ImageGalleryLayout3;
